/**
 * AthleteClaimCTA — Bloc public "Cette fiche vous concerne ?" (Sprint 3 Lot A.3)
 *
 * Affiché UNIQUEMENT sur les fiches institutionnelles non revendiquées :
 *   - owner_user_id absent
 *   - non supprimées / archivées (public_filter)
 *   - non fusionnées (merged_into null — garanti par le filtre public backend)
 *   - claim_status != claimed
 *
 * Parcours court :
 *   - Si non connecté → redirection /connexion?next=... avec retour automatique
 *   - Si connecté → dialogue avec formulaire minimal (texte uniquement, PAS d'upload)
 *   - POST /api/athletes/claims { athlete_id, proof: { club, federation, licence, birth_year, note } }
 *   - Retour : "Votre demande de revendication est en cours de validation."
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { UserCheck, Clock } from "lucide-react";
import { showError } from "@/lib/errors";

export function AthleteClaimCTA({ athlete }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [existingClaim, setExistingClaim] = useState(null); // claim de l'utilisateur courant
  const [form, setForm] = useState({ club: "", federation: "", licence: "", birth_year: "", note: "" });

  // Auto-ouverture du dialog après login : ?claim=1
  useEffect(() => {
    if (!athlete?.id) return;
    const params = new URLSearchParams(location.search);
    if (user && params.get("claim") === "1") {
      setOpen(true);
      // Nettoie le param pour ne pas ré-ouvrir en boucle
      const clean = new URLSearchParams(location.search);
      clean.delete("claim");
      const qs = clean.toString();
      nav(location.pathname + (qs ? `?${qs}` : ""), { replace: true });
    }
  }, [user, athlete?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Charge l'éventuel claim existant du user courant sur cet athlète
  useEffect(() => {
    if (!user || !athlete?.id) return;
    let cancelled = false;
    api.get("/athletes/claims/mine").then((r) => {
      if (cancelled) return;
      const mine = (r.data?.items || []).find((c) => c.athlete_id === athlete.id && c.status === "pending");
      setExistingClaim(mine || null);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [user, athlete?.id]);

  // Condition d'affichage : non revendiqué, non fusionné, non propriétaire
  const isClaimable = !!athlete
    && !athlete.owner_user_id
    && !athlete.merged_into
    && (athlete.claim_status || "unclaimed") !== "claimed";
  const isMyProfile = user && athlete?.owner_user_id === user.id;
  if (!isClaimable || isMyProfile) return null;

  const openClaim = () => {
    if (!user) {
      // Retour automatique avec ouverture auto du claim
      const next = encodeURIComponent(`${location.pathname}?claim=1`);
      nav(`/connexion?next=${next}`);
      return;
    }
    setOpen(true);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const proof = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== ""),
      );
      const { data } = await api.post("/athletes/claims", { athlete_id: athlete.id, proof });
      setExistingClaim(data.claim || { status: "pending", athlete_id: athlete.id });
      setOpen(false);
      toast.success("Demande enregistrée. Elle est en cours de validation.");
    } catch (e) { showError(e, { prefix: "Revendication :" }); }
    finally { setBusy(false); }
  };

  // Un claim pending existe déjà pour ce user : afficher juste l'état
  if (existingClaim) {
    return (
      <Card className="p-4 border-dashed border-[hsl(var(--aito-lagoon))]/40 bg-[hsl(var(--aito-lagoon))]/5" data-testid="claim-pending-banner">
        <div className="flex items-center gap-3">
          <Clock className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
          <div className="flex-1">
            <div className="font-display font-semibold text-sm">Votre demande de revendication est en cours de validation.</div>
            <div className="text-xs text-muted-foreground mt-0.5">Un administrateur COPF ou AITO.PF traitera votre demande.</div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-4 border-dashed border-[hsl(var(--aito-gold))]/50 bg-[hsl(var(--aito-gold))]/5" data-testid="claim-cta-card">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex-1">
            <div className="font-display font-semibold text-base">Cette fiche vous concerne&nbsp;?</div>
            <p className="text-xs text-muted-foreground mt-0.5">Vous êtes cet athlète&nbsp;? Revendiquez cette fiche pour la compléter et la gérer.</p>
          </div>
          <Button onClick={openClaim} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="claim-cta-button">
            <UserCheck className="h-4 w-4 mr-1.5" />Revendiquer ce profil
          </Button>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="claim-dialog">
          <DialogHeader>
            <DialogTitle>Revendiquer cette fiche</DialogTitle>
            <DialogDescription>
              Renseignez les informations utiles à la vérification. Aucune pièce d'identité n'est demandée.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label htmlFor="claim-club">Club</Label>
              <Input id="claim-club" value={form.club} onChange={(e) => setForm({ ...form, club: e.target.value })} placeholder={athlete.clubs?.[0] || "Ex : Seasiders 12h"} data-testid="claim-input-club" />
            </div>
            <div>
              <Label htmlFor="claim-federation">Fédération</Label>
              <Input id="claim-federation" value={form.federation} onChange={(e) => setForm({ ...form, federation: e.target.value })} placeholder={athlete.federations?.[0] || "Ex : Fédération Tahitienne"} data-testid="claim-input-federation" />
            </div>
            <div>
              <Label htmlFor="claim-licence">Licence / identifiant fédéral (si disponible)</Label>
              <Input id="claim-licence" value={form.licence} onChange={(e) => setForm({ ...form, licence: e.target.value })} placeholder="Optionnel" data-testid="claim-input-licence" />
            </div>
            <div>
              <Label htmlFor="claim-year">Année de naissance</Label>
              <Input id="claim-year" type="number" min={1900} max={new Date().getFullYear()} value={form.birth_year} onChange={(e) => setForm({ ...form, birth_year: e.target.value })} placeholder="Ex : 2001" data-testid="claim-input-year" />
            </div>
            <div>
              <Label htmlFor="claim-note">Précision complémentaire</Label>
              <Textarea id="claim-note" rows={3} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optionnel : contexte, résultat récent, personne de contact qui peut confirmer…" data-testid="claim-input-note" />
            </div>
            <p className="text-[11px] text-muted-foreground">
              Ces informations resteront confidentielles et ne serviront qu'à vérifier votre identité sportive.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
            <Button onClick={submit} disabled={busy} className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]" data-testid="claim-submit">
              {busy ? "Envoi…" : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default AthleteClaimCTA;
